/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { useVideoContext } from '../../contexts/videoProvider/videoContext';

export function useDominantSpeaker() {
  const { room, videoProvider } = useVideoContext();
  const [dominantSpeaker, setDominantSpeaker] = useState(room?.dominantSpeaker);

  useEffect(() => {
    if (room) {
      // Sometimes, the 'dominantSpeakerChanged' event can emit 'null', which means that
      // there is no dominant speaker. If we change the main participant when 'null' is
      // emitted, the effect can be jarring to the user. Here we ignore any 'null' values
      // and continue to display the previous dominant speaker as the main participant.
      const handleDominantSpeakerChanged = (newDominantSpeaker: VideoProvider.IParticipant) => {
        if (newDominantSpeaker !== null) {
          setDominantSpeaker(newDominantSpeaker);
        }
      };

      // Since 'null' values are ignored, we will need to listen for the 'participantDisconnected'
      // event, so we can set the dominantSpeaker to 'null' when they disconnect.
      const handleParticipantDisconnected = (participant: VideoProvider.IParticipant) => {
        setDominantSpeaker(prevDominantSpeaker => {
          return prevDominantSpeaker === participant ? null : prevDominantSpeaker;
        });
      };

      videoProvider.onDominantSpeakerChanged(handleDominantSpeakerChanged);
      room.on?.('participantDisconnected', handleParticipantDisconnected);
      return () => {
        videoProvider.offDominantSpeakerChanged(handleDominantSpeakerChanged);
        room.off?.('participantDisconnected', handleParticipantDisconnected);
      };
    }
    return () => {};
  }, [room]);

  return dominantSpeaker;
}
